import { AuthSession, type Account } from '../api/session';
import { isApiError } from '../api/client';
import { button, div, note, overlayPanel, textInput } from './dom';

// Account UI (seed, adapted per REUSE-MAP §6: the teams/jersey flows died with the football
// half). A hand-built DOM overlay over the game: Sign in → Account. All user strings render via
// textContent (T-SEC-006). The magic-link `?token` and `#join=CODE` deep-link boot logic is
// preserved: `#join=CODE` now means a ROOM join (plan §16.1) and is handed to the injected
// callback (H3 wires the real flow).

export interface AccountFlowsOpts {
  /** `#join=CODE` deep link seen at boot — join that room (H3; no-op until then is fine). */
  onJoinRoom: (code: string) => void;
  /**
   * The overlay closed (or boot's refresh settled): whatever sits beneath should re-read
   * session state. RE-READ, never surface: boot's refresh settles after a `?room=` boot has
   * already put a lobby or a board up, and bringing the menu forward here buried it (D255).
   */
  onStateChange?: () => void;
  /**
   * A magic link was just verified on a browser that was signed OUT a moment before: the one
   * moment a guest becomes an account (D256). Once per such transition, and never for
   * anything that merely re-reads a session — boot's refresh of a stored token, closing the
   * panel, or a link verified while already signed in (no guest became anybody there).
   */
  onSignedIn?: () => void;
  /**
   * Sign out was pressed, and this session's token is about to be revoked (D258). The logout
   * request waits for the returned promise — at most `SIGN_OUT_WAIT_MS` — so whatever must
   * still reach the server under the account (the telemetry queue) has LANDED while the token
   * verifies. Sending it first is not enough: a beacon issued ahead of the logout was
   * measured arriving behind it, and was filed under the browser's guest id.
   */
  onSigningOut?: () => Promise<void> | void;
  /**
   * Account deletion was confirmed (D258). Fired BEFORE the DELETE request: whatever is held on
   * the account's behalf is dropped, never sent, so nothing of the account can arrive after
   * the teardown that was meant to remove it.
   */
  onDeletingAccount?: () => void;
}

/**
 * D258: the longest Sign out waits for `onSigningOut` before revoking the token anyway. A
 * batch landing takes one round trip; a sign-out is never held hostage by telemetry.
 */
export const SIGN_OUT_WAIT_MS = 2000;

/** Run a hook and settle when its promise does or `ms` passes — whichever first; never rejects. */
function settleWithin(hook: (() => Promise<void> | void) | undefined, ms: number): Promise<void> {
  let landed: Promise<void>;
  try {
    landed = Promise.resolve(hook?.()).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    landed = Promise.resolve();
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cap = new Promise<void>((resolve) => (timer = setTimeout(resolve, ms)));
  return Promise.race([landed, cap]).finally(() => clearTimeout(timer));
}

export interface AccountFlows {
  openSignIn(): void;
  openAccount(): void;
  /** Honour a magic-link `?token` or a `#join=CODE` deep link; otherwise leave the overlay closed. */
  boot(): void;
}

export function createAccountFlows(session: AuthSession, opts: AccountFlowsOpts): AccountFlows {
  const { overlay, mount } = overlayPanel();
  overlay.style.display = 'none'; // mounts closed — the home screen beneath is the landing view
  // (D218: overlayPanel attaches itself now; this file used to be the only caller that
  // remembered to, which is exactly why the other two were silently broken.)
  const open = (): void => void (overlay.style.display = 'flex');
  const close = (): void => {
    overlay.style.display = 'none';
    opts.onStateChange?.();
  };

  const err = (e: unknown): string => (isApiError(e) ? `${e.code}: ${e.message}` : 'network error');
  const title = (): HTMLElement => {
    const h = div('display:flex;justify-content:space-between;align-items:baseline;margin-bottom:12px');
    // D239: the menu's own voice — Fredoka, cream on wood, not the FAFI display face
    h.append(div('font-family:var(--font-hud);font-weight:700;font-size:26px;letter-spacing:2px;color:var(--hud-wood-text)', 'KOLONY'));
    if (session.signedIn && session.account) h.append(div('font-size:12px;color:var(--ink-2)', session.account.displayName));
    return h;
  };

  // ── SIGN IN ──────────────────────────────────────────────────────────────────
  const signIn = (): void => {
    const c = div();
    c.append(title());
    c.append(div('font-size:21px;font-weight:700;margin:8px 0', 'Sign in'));
    c.append(div('font-size:13px;color:var(--ink-2);margin-bottom:6px', 'Optional — sign in to be ranked. Guests always play free.'));
    const email = textInput('you@example.com', '', 'email');
    const msg = note('');
    const send = button('Send magic link', () => {
      msg.textContent = 'sending…';
      session.api
        .post('/auth/magic-link', { email: email.value.trim() })
        .then(() => (msg.textContent = '✓ Check your email for the sign-in link (in dev, see the server console).'))
        .catch((e) => (msg.textContent = err(e)));
    }, { primary: true });
    c.append(email, send, msg);
    // dev/paste fallback: accept a magic-link token directly
    c.append(div('margin-top:14px;color:var(--ink-3);font-size:12px', 'Have a sign-in link/token? Paste it:'));
    const tok = textInput('token from the link');
    c.append(tok, button('Verify token', () => verifyToken(tok.value.trim())));
    c.append(div('margin-top:14px'), button('← Back', () => close()));
    mount(c);
  };

  const verifyToken = (token: string): void => {
    if (!token) return;
    // D256: read before the exchange. On the `?token` boot this runs after the stored
    // session's refresh has settled, so a token the server just refused already reads as
    // signed out — a lapsed session followed by a fresh link is a real sign-in.
    const wasSignedIn = session.signedIn;
    session
      .verify(token)
      .then(() => {
        if (!wasSignedIn) opts.onSignedIn?.();
        account();
      })
      .catch((e) => {
        const c = div();
        c.append(title(), note(`Sign-in failed — ${err(e)}`, 'var(--danger-ink)'), button('← Back', () => signIn()));
        mount(c);
      });
  };

  // ── ACCOUNT ──────────────────────────────────────────────────────────────────
  const account = (): void => {
    const a = session.account;
    if (!a) return void close();
    const c = div();
    c.append(title());
    c.append(div('font-size:21px;font-weight:700;margin:8px 0', 'Account'), div('color:var(--hud-ink-muted);font-size:13.5px', a.email));
    c.append(div('margin-top:10px', 'Display name'));
    const name = textInput('display name', a.displayName);
    const msg = note('');
    c.append(
      name,
      button('Save name', () => {
        session.api
          .patch<{ account: Account }>('/me', { displayName: name.value.trim() })
          .then((r) => ((session.account = r.account), (msg.textContent = '✓ saved')))
          .catch((e) => (msg.textContent = err(e)));
      }),
      msg,
    );
    c.append(
      div('margin-top:16px'),
      button('Sign out', () => {
        // D258: revoke only once what must land under the token has landed (or the cap passed)
        void settleWithin(opts.onSigningOut, SIGN_OUT_WAIT_MS)
          .then(() => session.signOut())
          .then(() => close());
      }),
    );
    c.append(div('margin-top:16px'));
    c.append(button('Delete account', () => confirmDelete()));
    c.append(div('margin-top:12px'), button('← Back', () => close()));
    mount(c);
  };

  const confirmDelete = (): void => {
    const c = div();
    c.append(title(), note('Delete your account? Your games remain recorded without your name. This cannot be undone.', 'var(--danger-ink)'));
    c.append(
      button('Yes, delete', () => {
        opts.onDeletingAccount?.(); // D258: before the request, so nothing held for the account outlives it
        void session.api
          .delete('/me')
          .then(() => (session.setToken(null), close()))
          .catch(() => close());
      }),
      button('Cancel', () => account()),
    );
    mount(c);
  };

  // ── boot: honour a magic-link ?token or a #join=CODE deep link (else stay closed) ──
  const boot = (): void => {
    const url = new URL(location.href);
    const magic = url.searchParams.get('token');
    const joinHash = location.hash.startsWith('#join=') ? location.hash.slice(6) : null;
    if (magic) {
      url.searchParams.delete('token');
      history.replaceState(null, '', url); // strip the token from the address bar
      open();
      const c = div();
      c.append(title(), note('verifying sign-in…'));
      mount(c);
      session.refresh().finally(() => verifyToken(magic));
    } else if (joinHash) {
      opts.onJoinRoom(joinHash); // room deep link (plan §16.1); session refresh rides along below
      if (session.signedIn) void session.refresh().finally(() => opts.onStateChange?.());
    } else if (session.signedIn) {
      session.refresh().finally(() => {
        opts.onStateChange?.(); // the refresh may have loaded (or 401-cleared) the account
      });
    }
    // signed out with no params: nothing to do — the home screen is already the landing view
  };

  return {
    openSignIn: () => (open(), signIn()),
    openAccount: () => (open(), account()),
    boot,
  };
}
